/**
 * React Router paths and `?query` conventions — keep in sync with `App.tsx` `<Routes>`.
 */

export const ROUTES = {
  home: "/",
  renderer: "/renderer",
  rendererWrapper: "/renderer-wrapper",
  dev: "/dev",
  login: "/login",
  register: "/register",
  playground: "/playground",
  extensions: "/extensions",
  download: "/download",
  downloadWeb: "/download-web",
  molarMass: "/molar-mass",
  fileUpload: "/file-upload",
  shop: "/shop",
  shopProduct: "/shop/p/:slug",
  cart: "/shop/cart",
  shopOrders: "/shop/orders",
  shopOrderDetail: "/shop/orders/:id",
  shopOrderSuccess: "/shop/orders/success",
  membership: "/membership",
  credits: "/credits",
  membershipElectron: "/membership-electron",
  selfIonization: "/self-ionization",
  quantumOrbitals: "/quantum-orbitals",
  blog: "/blog",
  article: "/article",
  privacy: "/privacy",
  cookies: "/cookies",
  license: "/license",
  terms: "/terms",
  support: "/support",
  balance: "/balance",
  docs: "/docs",
  periodicTable: "/periodic-table",
  compare: "/compare",
  reaction: "/reaction",
  stoichiometry: "/stoichiometry",
  acidBase: "/acid-base",
  quiz: "/quiz",
  configBuilder: "/config-builder",
} as const;

export type AppRoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Query keys used across Table, Renderer, App shell, Playground, Footer. */
export const APP_QUERY = {
  fullscreen: "fullscreen",
  keyword: "keyword",
  q: "q",
  searchbar: "searchbar",
} as const;

export type AppQueryFlags = {
  fullscreen: boolean;
  searchbar: boolean;
  presentation: boolean;
  q: string | null;
  keyword: string | null;
};

export function parseAppQuery(search: string | URLSearchParams): AppQueryFlags {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  return {
    fullscreen: params.get(APP_QUERY.fullscreen) === "true",
    searchbar: params.get(APP_QUERY.searchbar) === "true",
    presentation: params.get("presentation") === "true",
    q: params.get(APP_QUERY.q),
    keyword: params.get(APP_QUERY.keyword),
  };
}

/** `/?searchbar=true&q=...` */
export function buildHomeSearchUrl(searchText: string): string {
  const p = new URLSearchParams();
  p.set(APP_QUERY.searchbar, "true");
  p.set(APP_QUERY.q, searchText);
  return `${ROUTES.home}?${p.toString()}`;
}

/** `/renderer?fullscreen=true&keyword=...` */
export function buildRendererSearch(
  options?: { fullscreen?: boolean; keyword?: string }
): string {
  const p = new URLSearchParams();
  if (options?.fullscreen) p.set(APP_QUERY.fullscreen, "true");
  if (options?.keyword) p.set(APP_QUERY.keyword, options.keyword);
  const qs = p.toString();
  return qs ? `${ROUTES.renderer}?${qs}` : ROUTES.renderer;
}
